import { cloneDeep, isEmpty } from 'lodash-es';
import React from 'react';

import { SelectPill } from '~/src/dash/components/dropdowns/SelectPill';
import {
  FILTER_PILLS,
  FilterPillKey,
  ICustomAPIFilterConfig,
} from '~/src/dash/components/filters/common';
import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import { Col, Cols, spacing, TextButton } from '~/src/dash/publicaUI';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { IActiveFilters } from '~/src/dash/views/dashboards/DashboardHome/common/common';

const isPublicaFilterPillDisabled = (
  activeFilters: IActiveFilters,
  _key: string,
  category?: string,
) => {
  const { demand, supply, device } = activeFilters;
  switch (category) {
    case 'publisher':
      if (supply === 'Clusters') return false;
      if (supply) return true;
      break;
    case 'supply':
      if (device) return true;
      break;
    case 'demand':
      if (supply === 'Clusters') return false;
      if (supply || device) return true;
      break;
    case 'device':
      if (supply || demand) return true;
      break;
    default:
      return false;
  }
  return false;
};

const isFilterPillDisabled = (
  activeFilters: IActiveFilters,
  key: string,
  category?: string,
  isLive?: boolean,
  isPublicaAnalytics?: boolean,
  isDemandSourceUsingBidderType?: boolean,
) => {
  if (isPublicaAnalytics) return isPublicaFilterPillDisabled(activeFilters, key, category);
  const { demand, supply, device, deal } = activeFilters;
  const isDealActive = isLive && !!deal; // deal is an edge case since it's only used in live dash
  switch (category) {
    case 'demand':
      // this solves for an edge case. demand sources can be linked with device type
      const isDemandSourceFilter = key === 'Filtered Demand Sources';
      if ((device && !isDemandSourceFilter) || isDealActive) {
        return true;
      }
      if ((supply && device) || isDealActive) {
        return true;
      }
      break;
    case 'supply':
      if ((demand && device) || isDealActive) {
        return true;
      }
      break;
    case 'device':
      if (
        isDemandSourceUsingBidderType ||
        (demand && demand !== 'Filtered Demand Sources') ||
        isDealActive
      ) {
        return true;
      }
      break;
    case 'deal':
      if (demand || supply || device) {
        return true;
      }
    default:
      return false;
  }
  return false;
};

const isClearAllDisplayed = (selectedFilters: Record<string, IMenuList>) => {
  return Object.entries(selectedFilters).some(([key, filters]) =>
    key === 'Timezones' ? false : !isEmpty(filters),
  );
};

interface IDashboardFilterPillsProps {
  cfg: ICustomAPIFilterConfig;
  activeFilters: IActiveFilters;
  handleClearAll: () => void;
  filters: Record<string, IMenuList>;
  selectedFilters: Record<string, IMenuList>;
  handleAccept: (
    key: string,
    allOptions: Record<string, IListItem>,
    selectedOptions: Record<string, IListItem>,
  ) => void;
  handleOpenFilter?: () => void;
  handleCloseFilter?: (hasChangeOccurred?: boolean) => void;
  orderedFilterKeys: FilterPillKey[];
  loadState: LoadingState;
  handleChangeTimezone: (
    key: string,
    allOptions: Record<string, IListItem>,
    selectedOptions: Record<string, IListItem>,
  ) => void;
  isDemandRefreshing?: boolean;
  timezone: string;
  isLive?: boolean;
  isPublicaAnalytics?: boolean;
  isDemandSourceUsingBidderType?: boolean;
}

export const DashboardFilterPills = ({
  activeFilters,
  cfg,
  filters,
  handleAccept,
  handleChangeTimezone,
  handleClearAll,
  handleCloseFilter,
  handleOpenFilter,
  isDemandRefreshing,
  isLive,
  isPublicaAnalytics,
  loadState,
  orderedFilterKeys,
  selectedFilters,
  timezone,
  isDemandSourceUsingBidderType,
}: IDashboardFilterPillsProps) => {
  const isLoading = loadState === LoadingStateLoading;
  return (
    <Cols wrap gap={spacing.small} style={{ alignItems: 'center', rowGap: spacing.extraSmall }}>
      {isClearAllDisplayed(selectedFilters) && (
        <Col style={{ padding: '0px 8px' }}>
          <TextButton
            size='small'
            style={{ fontWeight: FONT_WEIGHT.base }}
            onClick={handleClearAll}
          >
            Clear All
          </TextButton>
        </Col>
      )}
      {orderedFilterKeys.map((key) => {
        const { category } = cfg[key];
        const isRefreshing =
          (key === 'Filtered Demand Sources' || key === 'Bidders') && isDemandRefreshing;
        const { icon } = FILTER_PILLS[key as FilterPillKey];
        const isDisabled = isFilterPillDisabled(
          activeFilters,
          key,
          category,
          isLive,
          isPublicaAnalytics,
          isDemandSourceUsingBidderType,
        );
        return (
          <Col key={key}>
            <SelectPill
              currentSelected={selectedFilters[key] || {}}
              displayID={cfg[key].displayID || false}
              filterKey={key}
              handleAccept={handleAccept}
              icon={icon}
              isActive={!!category && activeFilters[category] === key}
              isDisabled={isDisabled}
              isSubset={cfg[key].isSubset}
              options={cloneDeep(filters[key])}
              selectedLimit={cfg[key].limit}
              isRefreshing={isLoading || isRefreshing}
              tooltip={isDisabled ? 'Unavailable due to the selected filters.' : ''}
              handleOnOpen={handleOpenFilter}
              handleOnClose={handleCloseFilter}
              disabledOptionTooltip='Unavailable due to the selected filters.'
              enableActiveFilter={cfg[key].isActiveStatusEnabled}
            />
          </Col>
        );
      })}
      {/* timezone is a special case and has to be handled separately */}
      <Col>
        <SelectPill
          currentSelected={{ [timezone]: { ID: timezone, selected: true, title: timezone } }}
          customLabel={`Timezone: ${timezone}`}
          filterKey={'Timezones'}
          handleAccept={handleChangeTimezone}
          icon='clock'
          isActive={false}
          options={cloneDeep(filters.Timezones)}
          selectedLimit={1}
          isRefreshing={isLoading}
          handleOnOpen={handleOpenFilter}
          handleOnClose={handleCloseFilter}
          isSelectionRequired={true}
        />
      </Col>
    </Cols>
  );
};
